import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import ActivityCreator from '../../../activity-creator/ActivityCreator';
import { useTranslation } from '../../../../shared/i18n';

/**
 * ActivityCreatorWrapper - Wraps ActivityCreator for use in React Router
 * Fetches child data and handles navigation callbacks
 */
export const ActivityCreatorWrapper = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [childUsername, setChildUsername] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get CSRF token from meta tag
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';

  // Get Rails environment from window global (set by Rails view)
  // For React Native Web builds, check EXPO_PUBLIC_DEV_MODE (set at build time by Metro)
  // Fall back to production if neither is available
  const getRailsEnv = () => {
    if (typeof window !== 'undefined' && window.railsEnv) {
      return window.railsEnv;
    }
    // EXPO_PUBLIC_DEV_MODE is inlined at build time by Metro bundler
    // Metro replaces process.env.EXPO_PUBLIC_DEV_MODE with the literal value
    // eslint-disable-next-line no-undef
    try {
      if (process.env.EXPO_PUBLIC_DEV_MODE === 'true') {
        return 'development';
      }
    } catch {
      // process might not exist in some browser contexts
    }
    return 'production';
  };
  const railsEnv = getRailsEnv();

  // Fetch child data to get username
  useEffect(() => {
    const fetchChildData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`/api/children/${childId}/curriculum`, {
          headers: {
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch child data');
        }

        const data = await response.json();
        setChildUsername(data.child?.username || '');
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    };

    if (childId) {
      fetchChildData();
    }
  }, [childId, csrfToken]);

  // Handle activity creation completion
  const handleComplete = (activity) => {
    // Invalidate the curriculum cache so it refetches after navigation
    queryClient.invalidateQueries({ queryKey: ['curriculum', childId] });
    // Navigate to curriculum page with success notice
    navigate(`/children/${childId}/curriculum?notice=Activity+added+successfully`);
  };

  // Handle cancellation
  const handleCancel = () => {
    // Navigate back to curriculum page
    navigate(`/children/${childId}/curriculum`);
  };

  if (isLoading) {
    return (
      <div className="bg-light min-vh-100">
        <div className="container py-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-light min-vh-100">
        <div className="container py-4">
          <div className="alert alert-danger">
            {t('common.error')}: {error}
          </div>
          <button className="btn btn-outline-secondary" onClick={handleCancel}>
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <ActivityCreator
      childId={childId}
      childUsername={childUsername}
      railsEnv={railsEnv}
      csrfToken={csrfToken}
      onComplete={handleComplete}
      onCancel={handleCancel}
    />
  );
};

export default ActivityCreatorWrapper;
